import { useState } from 'react';
import { LoaderCircle, LogOut, ShieldAlert, ShieldX } from 'lucide-react';
import { Button } from '../components/button.jsx';
import { AuthLayout } from './auth-layout.jsx';
import { TwoFactorSettings } from './two-factor-settings.jsx';
import { Alert, AlertDescription } from '../components/alert.jsx';

/** @param {{ label?: string }} props */
export function FullPageSpinner({ label = 'Wird geladen …' }) {
  return (
    <div className="flex min-h-dvh items-center justify-center text-muted-foreground" role="status" aria-live="polite">
      <LoaderCircle className="size-6 animate-spin" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/**
 * Shown when the user is logged in but lacks access to this application.
 * @param {{ appName: string, name?: string, otherApp?: { label: string, href: string } | null, onLogout: () => void }} props
 */
export function NoAccessPage({ appName, name, otherApp, onLogout }) {
  return (
    <AuthLayout appName={appName} title="Kein Zugang zu dieser Anwendung">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <ShieldX className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            {name ? `${name}, Ihr` : 'Ihr'} Benutzerkonto ist für {appName} nicht freigeschaltet. Wenn Sie glauben, dass dies ein Fehler ist, wenden Sie sich
            bitte an Ihre Verwaltung.
          </p>
        </div>
        {otherApp && (
          <Button asChild>
            <a href={otherApp.href}>{otherApp.label}</a>
          </Button>
        )}
        <Button variant="outline" onClick={onLogout}>
          <LogOut aria-hidden="true" /> Abmelden
        </Button>
      </div>
    </AuthLayout>
  );
}

/**
 * Two-factor setup is mandatory for the user's role but not confirmed yet.
 * @param {{ appName: string, actions: import('./two-factor-settings.jsx').TwoFactorActions, onDone?: () => void | Promise<unknown>, onLogout: () => void }} props
 */
export function TwoFactorRequiredPage({ appName, actions, onDone, onLogout }) {
  const [completed, setCompleted] = useState(false);
  return (
    <AuthLayout appName={appName} title="2FA einrichten" description="Für Ihre Rolle ist die Zwei-Faktor-Authentifizierung verpflichtend.">
      <div className="flex flex-col gap-4">
        <Alert variant="warning">
          <ShieldAlert aria-hidden="true" />
          <AlertDescription>Bitte richten Sie die Zwei-Faktor-Authentifizierung ein, um fortzufahren.</AlertDescription>
        </Alert>
        <TwoFactorSettings enabled={completed} required actions={actions} onChanged={() => setCompleted(true)} />
        {completed ? (
          <Button onClick={onDone}>Weiter zur Anwendung</Button>
        ) : (
          <Button variant="ghost" onClick={onLogout} className="self-start">
            <LogOut aria-hidden="true" /> Abmelden
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}
